import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useWebsocketStore } from '../../../stores/websocketStore/websocketProvider';
import style from './ChangePasswordForm.module.css';
import { Body1, Body3 } from '../../atomic/text/Text';
import TextButton from '../../atomic/buttons/textButton/TextButton';
import { colors } from '../../../colorVars';
import { IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';

export const ChangePasswordForm = observer(({ onClose }) => {
  const { userAuthStore } = useWebsocketStore();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await userAuthStore.changePassword(oldPassword, newPassword);

      if (result.success) {
        setSuccess(true);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.error || 'Failed to change password');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={style.container}>
      <Body1 color={colors.lightGrey} fontWeight={700}>
        Change Password
      </Body1>

      <form onSubmit={handleSubmit} className={style.form}>
        <div className={style.inputGroup}>
          <label htmlFor="oldPassword" className={style.label}>
            <Body3 color={colors.lightGrey} fontWeight={500}>
              Current Password
            </Body3>
          </label>
          <div className={style.passwordInputWrapper}>
            <input
              id="oldPassword"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              required
              autoComplete="current-password"
              className={style.input}
              disabled={isLoading}
            />
            <IconButton
              onClick={() => setShowOldPassword(!showOldPassword)}
              className={style.visibilityButton}
              size="small"
            >
              {showOldPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
        </div>

        <div className={style.inputGroup}>
          <label htmlFor="newPassword" className={style.label}>
            <Body3 color={colors.lightGrey} fontWeight={500}>
              New Password
            </Body3>
          </label>
          <div className={style.passwordInputWrapper}>
            <input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="off"
              className={style.input}
              disabled={isLoading}
            />
            <IconButton
              onClick={() => setShowNewPassword(!showNewPassword)}
              className={style.visibilityButton}
              size="small"
            >
              {showNewPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
        </div>

        <div className={style.inputGroup}>
          <label htmlFor="confirmPassword" className={style.label}>
            <Body3 color={colors.lightGrey} fontWeight={500}>
              Confirm New Password
            </Body3>
          </label>
          <div className={style.passwordInputWrapper}>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="off"
              className={style.input}
              disabled={isLoading}
            />
            <IconButton
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={style.visibilityButton}
              size="small"
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>
        </div>

        {error && (
          <div className={style.errorMessage}>
            <Body3 color={colors.red} fontWeight={500}>
              {error}
            </Body3>
          </div>
        )}

        {success && (
          <div className={style.successMessage}>
            <Body3 color={colors.green} fontWeight={500}>
              Password changed successfully!
            </Body3>
          </div>
        )}

        <div className={style.buttons}>
          <TextButton
            type="submit"
            width="48%"
            fontSize="medium"
            disabled={isLoading}
          >
            {isLoading ? 'Changing...' : 'Change Password'}
          </TextButton>
          {onClose && (
            <TextButton
              type="button"
              width="48%"
              fontSize="medium"
              backgroundColor={colors.grey}
              clickHandler={onClose}
              disabled={isLoading}
            >
              Cancel
            </TextButton>
          )}
        </div>
      </form>
    </div>
  );
});
